import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, BehaviorSubject, timer } from 'rxjs';
import { switchMap, tap, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { environment } from 'src/environments/environment';
import { 
  Notification, 
  DashboardNotificationResponse, 
  AllNotificationsResponse,
  MarkReadResponse,
  NotificationSummary 
} from '../models/notification.models';

@Injectable({
  providedIn: 'root'
})
export class NotificationsService {
  private readonly notificationsUrl = environment.main_url + '/agilelegal/notifications';
  
  // Subject to track notification summary
  private notificationSummarySubject = new BehaviorSubject<NotificationSummary>({
    unreadCount: 0,
    hasMore: false,
    notifications: []
  });
  public notificationSummary$ = this.notificationSummarySubject.asObservable();

  // Polling subscription
  private pollingEnabled = false;

  constructor(private readonly http: HttpClient) { }

  /**
   * Get dashboard notifications
   */
  getDashboardNotifications(limit?:number): Observable<DashboardNotificationResponse> {
    if (limit) {
      return this.http.get<DashboardNotificationResponse>(`${this.notificationsUrl}/dashboard`, {
        params: new HttpParams().set('limit', limit.toString())
      });
    }
    return this.http.get<DashboardNotificationResponse>(`${this.notificationsUrl}/dashboard`);
  }

  /**
   * Get all notifications with pagination
   */
  getAllNotifications(page: number = 1, size: number = 20): Observable<AllNotificationsResponse> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());
    
    return this.http.get<AllNotificationsResponse>(`${this.notificationsUrl}/all`, { params });
  }

  /**
   * Mark notification as read
   */
  markNotificationAsRead(id: number): Observable<MarkReadResponse> {
    return this.http.put<MarkReadResponse>(`${this.notificationsUrl}/${id}/mark-read`, {});
  }

  dismissNotification(id: number): Observable<MarkReadResponse> {
    return this.http.put<MarkReadResponse>(`${this.notificationsUrl}/${id}/dismiss`, {});
  }

  markReminderNotificationAsRead(id: number,payload:any): Observable<MarkReadResponse> {
    return this.http.put<MarkReadResponse>(`${this.notificationsUrl}/${id}/mark-read`, payload);
  }

  /**
   * Load dashboard notifications and update summary
   */
  loadDashboardNotifications(): Observable<NotificationSummary> {
    return this.getDashboardNotifications().pipe(
      tap(response => {
        if (response.data) {
          const summary: NotificationSummary = {
            unreadCount: response.data.unreadCount,
            hasMore: response.data.hasMore,
            notifications: response.data.notifications
          };
          this.notificationSummarySubject.next(summary);
        }
      }),
      switchMap(response => of(this.notificationSummarySubject.value)),
      catchError(error => {
        console.error('Error loading dashboard notifications:', error);
        return of(this.notificationSummarySubject.value);
      })
    );
  }

  /**
   * Load ALL notifications and update summary
   */
  loadAllNotifications(limit: number): Observable<NotificationSummary> {
    return this.getDashboardNotifications(limit).pipe(
      tap(response => {
        if (response.data) {
          const summary: NotificationSummary = {
            unreadCount: response.data.unreadCount,
            hasMore: false, // Since we're loading all
            notifications: response.data.notifications
          };
          this.notificationSummarySubject.next(summary);
        }
      }),
      switchMap(response => of(this.notificationSummarySubject.value)),
      catchError(error => {
        console.error('Error loading all notifications:', error);
        return of(this.notificationSummarySubject.value);
      })
    );
  }

  /**
   * Start polling for notifications every 30 seconds
   */
  startPolling(): void {
    if (this.pollingEnabled) {
      return; // Already polling
    }
    
    this.pollingEnabled = true;
    
    // Initial load
    this.loadDashboardNotifications().subscribe();
    
    // Start polling every 60 seconds (1 minute)
    timer(60000, 60000).pipe(
      switchMap(() => this.loadDashboardNotifications()),
      catchError(error => {
        console.error('Polling error:', error);
        return of(null);
      })
    ).subscribe();
  }

  /**
   * Stop polling for notifications
   */
  stopPolling(): void {
    this.pollingEnabled = false;
  }

  /**
   * Mark notification as read and update summary
   */
  markAsReadAndUpdate(notificationId: number): Observable<boolean> {
    return this.markNotificationAsRead(notificationId).pipe(
      tap(response => {
        if (response.data) {
          // Reload dashboard notifications to get updated counts
          this.loadDashboardNotifications().subscribe();
        }
      }),
      switchMap(response => of(response.data || false)),
      catchError(error => {
        console.error('Error marking notification as read:', error);
        return of(false);
      })
    );
  }

  /**
   * Get current notification summary
   */
  getCurrentSummary(): NotificationSummary {
    return this.notificationSummarySubject.value;
  }

  /**
   * Helper method to get notification message text
   */
  getNotificationMessage(notification: Notification): string {
    const targetType = notification.comment.targetType.toLowerCase();
    const createdBy = notification.comment.createdBy;
    
    return `${createdBy} commented on ${targetType} #${notification.comment.targetId}`;
  }

  /**
   * Helper method to get notification time ago
   */
  getTimeAgo(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.error('Invalid date string:', dateString);
      return 'Unknown time';
    }
    
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 0) {
      return 'just now'; // Handle future dates
    } else if (diffInSeconds < 60) {
      return diffInSeconds <= 1 ? 'just now' : `${diffInSeconds} seconds ago`;
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 2592000) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} day${days > 1 ? 's' : ''} ago`;
    } else {
      return date.toLocaleDateString();
    }
  }

  /**
   * Helper method to truncate comment text for notifications
   */
  truncateText(text: string, maxLength: number = 50): string {
    if (text.length <= maxLength) {
      return text;
    }
    return text.substring(0, maxLength) + '...';
  }

  getNotificationsPaginated(pageNumber: any, pageSize: any,payload:any){
    if(pageNumber)
     return this.http.post<any>(this.notificationsUrl+"/filter?pageNumber="+pageNumber+"&pageSize="+pageSize,payload)
    else
      return this.http.post<any>(this.notificationsUrl+"/filter",payload)
  }
}
